<template>
  <div class="grid-map-menu-body">
    <tab-form
      :tabList="tabList"
      :changeSelectedTabIndex="changeSelectedTabIndex"
      :selectedTabIndex="selectedTabIndex"
    />
    <search-form
      class="grid-map-search-form"
      placeholder="キーワードを入力"
      :keyword="keyword"
      :handleInput="searchEquipmentBaseStations"
    />
    <component
      :is="scrollbarTag"
      class="grid-map-scroll-area--customizer pb-10"
      :settings="settings"
      :key="$vs.rtl"
    >
      <ul class="pb-2 mt-1">
        <div v-if="selectedTabIndex === 0 && isReady">
          <li
            class="pb-2 pl-4 pr-4 mb-2"
            v-for="vessel in resultEquipmentVessels"
            :key="vessel.vessel_id"
          >
            <equipment-vessel-card
              :vessel="vessel"
              :jumpToEquipmentVessel="jumpToEquipmentVessel"
              :isSelected="vessel.vessel_id === trackingVesselTarget"
              :seaRoute="getSortedSeaRoutes(vessel)"
              :isDebug="isDebug"
            ></equipment-vessel-card>
          </li>
        </div>
        <div v-if="selectedTabIndex === 1">
          <li
            class="pb-2 pl-4 pr-4 mb-2"
            v-for="baseStation in resultBaseStations"
            :key="baseStation.hash"
          >
            <base-station-card
              :baseStation="baseStation"
              :equipmentTypesMap="equipmentTypesMap"
              :jumpToBaseStation="jumpToBaseStation"
              :isSelected="baseStation.hash === trackingBaseTarget"
            ></base-station-card>
          </li>
        </div>
        <div v-if="selectedTabIndex === 2">
          <li
            class="pb-2 pl-4 pr-4 mb-2"
            v-for="equipmentType in resultEquipmentTypes"
            :key="equipmentType.hash"
          >
            <equipment-type-card
              :equipmentType="equipmentType"
            ></equipment-type-card>
          </li>
        </div>
      </ul>
    </component>
  </div>
</template>

<script>
/**
 * マップ右メニューの拠点一覧と検索フォームを描画するコンポーネント
 * @parent O.RightMenuBase
 */
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import SearchForm from "@/components/molecules/SearchForm";
import FireStoreService from "@/services/fireStoreService";
import { createNamespacedHelpers } from "vuex";
import TabForm from "@/components/molecules/TabForm";
import BaseStationCard from "@/components/molecules/BaseStationCard";
import EquipmentTypeCard from "@/components/molecules/EquipmentTypeCard";
import EquipmentVesselCard from "@/components/molecules/EquipmentVesselCard";
import LocalDbService from "@/services/localDbService";
import _ from "lodash";
import * as turf from "@turf/turf";
import { isEquipmentVesselCompleteInput } from "@/utils/VesselUtil";
import moment from "moment-timezone";
import { DateUtil } from "@/utils";
const INTERVAL_EQUIPMENT_VESSEL_INFO = 10 * 60 * 1000;
let intervalEquipmentVesselInfoId;

const {
  mapMutations: mapMutationsOfLiveMap,
  mapGetters: mapGettersOfLiveMap,
  mapActions: mapActionsOfLiveMap,
} = createNamespacedHelpers("liveMap");

const {
  mapMutations: mapMutationsOfGridMap,
  mapState: mapStateOfGridMap,
  mapGetters: mapGettersOfGridMap,
} = createNamespacedHelpers("gridMap");

const {
  mapState: mapStateOfVesselEquipmentBaseStation,
  mapGetters: mapGettersOfVesselEquipmentBaseStation,
} = createNamespacedHelpers("gkmgVesselEquipmentBaseStation");

export default {
  name: "O.EquipmentBaseStation",
  components: {
    VuePerfectScrollbar,
    SearchForm,
    TabForm,
    EquipmentVesselCard,
    BaseStationCard,
    EquipmentTypeCard,
  },
  props: {
    /** スクロールの設定 */
    settings: {
      type: Object,
      required: true,
    },
    vesselPositions: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      tabList: [
        {
          label: "作業船",
        },
        {
          label: "石材ストック",
        },
        {
          label: "投入実績",
        },
      ],
      // 検索ワード
      keyword: "",
      // 選択されているタブのインデックス番号
      selectedTabIndex: 0,
      // FireStoreのリッスン解放
      unsubscribes: [],
      // 検索された石材ストック情報一覧
      searchedBaseStations: [],
      // 検索された作業船一覧
      searchedEquipmentVessels: [],
      // 検索された投入実績一覧
      searchedEquipmentTypes: [],
      expiredPopup: null,
      timerId: null,
      isDebug: false,
      isReady: false,
      vesselDetails: [],
      vesselDetailRequestingIds: new Set(), // 船舶詳細をリクエスト中のidの集合
      currentDate: new Date(),
    };
  },
  async mounted() {
    this.isDebug = await LocalDbService.getIsDebug();
    this.isReady = true;
    intervalEquipmentVesselInfoId = window.setInterval(() => {
      this.currentDate = new Date();
    }, INTERVAL_EQUIPMENT_VESSEL_INFO);
  },
  computed: {
    scrollbarTag() {
      return this.$store.state.is_touch_device ? "div" : "VuePerfectScrollbar";
    },
    ...mapGettersOfLiveMap(["getLastVesselPosition"]),
    ...mapStateOfGridMap({
      activeSidebarForGrid: (state) => state.activeSidebarForGrid,
    }),
    ...mapStateOfVesselEquipmentBaseStation({
      equipmentBaseStations: (state) => state.equipmentBaseStations,
      equipmentVessels: (state) => state.equipmentVessels,
      equipmentTypes: (state) => state.equipmentTypes,
      equipmentCategories: (state) => state.equipmentCategories,
      equipmentDestinations: (state) => state.equipmentDestinations,
      equipmentSeaRoutes: (state) => state.equipmentSeaRoutes,
    }),
    ...mapGettersOfGridMap(["getTrackingTarget"]),
    ...mapGettersOfVesselEquipmentBaseStation([
      "getEquipmentTypes",
      "getSortedBaseStations",
      "getSortedSeaRoutes",
    ]),
    // 作業船情報
    equipmentVesselInfo() {
      if (!this.equipmentVessels || !this.equipmentVessels.length) {
        return [];
      }
      const equipmentVessels = this.equipmentVessels.reduce((prev, vessel) => {
        // 船舶情報
        const targetVessel = this.vesselPositionsMap[vessel.vessel_id];
        const vesselInfo =
          targetVessel && targetVessel.aisInfo ? targetVessel.aisInfo : null;
        const vesselPosition =
          targetVessel && targetVessel.vesselPosition
            ? targetVessel.vesselPosition
            : null;

        // 積載物名
        // TODO: 一つの船舶に複数の積載物が表示される可能性あり
        const category = vessel.gkmg_equipment_category_ids.reduce(
          (acc, categoryId) => {
            if (
              this.equipmentCategoriesMap &&
              this.equipmentCategoriesMap[categoryId]
            ) {
              acc.push(this.equipmentCategoriesMap[categoryId]);
            }
            return acc;
          },
          [],
        )[0];
        const categoryLabel = category && category.label ? category.label : "";

        // 目的地
        const destination =
          this.equipmentDestinationsMap[
            vessel.data.gkmg_equipment_destination_id
          ];
        const destinationLabel =
          destination && destination.label ? destination.label : "";

        // 積載物の単位
        const equipmentType =
          this.equipmentTypesMap[vessel.data.equipment_type_id];
        const unit =
          equipmentType && equipmentType.unit ? equipmentType.unit : "";

        // 航海ステータスが「投入完了」かつ投入後12時間以上経過の場合はカードを非表示にする
        const isComplete = isEquipmentVesselCompleteInput(vessel.status);
        const isMonitored = this.isMonitored(
          isComplete,
          vessel.data.input_completion_datetime,
          this.currentDate,
        );
        const vesselData = {
          ...vessel,
          vesselDetail: this.vesselDetailsMap[vessel.vessel_id],
          vesselInfo: vesselInfo,
          vesselPosition: vesselPosition,
          categoryLabel: categoryLabel,
          destinationLabel: destinationLabel,
          unit: unit,
        };
        if (isMonitored) {
          prev.push(vesselData);
        }
        return prev;
      }, []);
      // 航海ステータス（航行中、投入中、投入完了）の順にソートする
      // 航海ステータスが同じ場合は出発時刻でソートする
      return _.orderBy(
        equipmentVessels,
        [
          (item) => {
            return item.status;
          },
          (item) => {
            if (!item.data) return null;
            if (!item.data.departure_datetime) return null;
            if (!item.data.departure_datetime.seconds) return null;
            return item.data.departure_datetime.seconds;
          },
        ],
        ["asc", "asc"],
      );
    },
    equipmentTypesMap() {
      if (!this.equipmentTypes) return {};
      return this.equipmentTypes.reduce((acc, type) => {
        if (type && type.id) {
          acc[type.id] = type;
        }
        return acc;
      }, {});
    },
    vesselPositionsMap() {
      if (!this.vesselPositions) return {};
      return this.vesselPositions.reduce((acc, vessel) => {
        if (vessel && vessel.vesselId) {
          acc[vessel.vesselId] = vessel;
        }
        return acc;
      }, {});
    },
    vesselDetailsMap() {
      if (!this.vesselDetails) return {};
      return this.vesselDetails.reduce((acc, vessel) => {
        if (vessel && vessel.id) {
          acc[vessel.id] = vessel;
        }
        return acc;
      }, {});
    },
    equipmentCategoriesMap() {
      if (!this.equipmentCategories) return {};
      return this.equipmentCategories.reduce((acc, category) => {
        if (category && category.id) {
          acc[category.id] = category;
        }
        return acc;
      }, {});
    },
    equipmentDestinationsMap() {
      if (!this.equipmentDestinations) return {};
      return this.equipmentDestinations.reduce((acc, destination) => {
        if (destination && destination.id) {
          acc[destination.id] = destination;
        }
        return acc;
      }, {});
    },
    resultEquipmentVessels() {
      return this.keyword.length
        ? this.searchedEquipmentVessels
        : this.equipmentVesselInfo;
    },
    resultBaseStations() {
      return this.keyword.length
        ? this.searchedBaseStations
        : this.getSortedBaseStations;
    },
    resultEquipmentTypes() {
      return this.keyword.length
        ? this.searchedEquipmentTypes
        : this.getEquipmentTypes;
    },
    // FireStoreで追跡設定しているターゲットがvesselならIDを返す
    trackingVesselTarget() {
      return this.getTrackingTarget({
        mapId: this.mapId,
        type: "vessel",
      });
    },
    // FireStoreで追跡設定しているターゲットが拠点ならIDを返す
    trackingBaseTarget() {
      return this.getTrackingTarget({
        mapId: this.mapId,
        type: "base",
      });
    },
    mapId() {
      if (!this.activeSidebarForGrid || !this.activeSidebarForGrid.mapId)
        return "";
      return this.activeSidebarForGrid.mapId;
    },
  },
  methods: {
    ...mapActionsOfLiveMap(["fetchLastVesselPositions", "getVessel"]),
    ...mapMutationsOfGridMap([
      "UPDATE_GRID_MAP_SETTINGS",
      "UPDATE_LAST_TRACKING_POSITION",
      "SET_EXPIRED_POPUP_FOR_GRID",
    ]),
    ...mapMutationsOfLiveMap(["REMOVE_LAST_VESSEL_POSITION"]),
    // 投入完了かつ投入完了から12時間以上経過しているとfalse
    // それ以外はtrue
    isMonitored(
      isComplete, // boolean
      inputCompletionDatetime, // number
      currentDate, // Date
    ) {
      if (!isComplete) return true;
      if (!inputCompletionDatetime) return true;
      if (!inputCompletionDatetime.seconds) return true;
      if (!currentDate) return true;
      return moment
        .unix(inputCompletionDatetime.seconds)
        .add(12, "hours")
        .isAfter(moment(currentDate), "second");
    },
    // 船の座標と目的地の座標との直線距離を求める。
    getDestinationDistance(vesselPosition, destinationPoint) {
      if (
        !vesselPosition ||
        !vesselPosition.l ||
        vesselPosition.l.longitude == null ||
        vesselPosition.l.latitude == null
      ) {
        return null;
      }
      if (
        !destinationPoint ||
        destinationPoint.longitude == null ||
        destinationPoint.latitude == null
      ) {
        return null;
      }
      const vesselCoordinate = [
        vesselPosition.l.longitude,
        vesselPosition.l.latitude,
      ];
      const destinationCoordinate = [
        destinationPoint.longitude,
        destinationPoint.latitude,
      ];
      return turf.distance(destinationCoordinate, vesselCoordinate, {
        units: "kilometers",
      });
    },
    // 検索用メソッド
    searchEquipmentBaseStations(nextKeyword) {
      this.keyword = nextKeyword;
      switch (this.selectedTabIndex) {
        // 作業船
        case 0: {
          const filteredEquipmentVessels = this.equipmentVesselInfo.filter(
            (vessel) => {
              const searchTargetArray = [
                vessel.vesselDetail.name_ja,
                vessel.vesselDetail.name,
              ];
              return searchTargetArray.some((item) => {
                if (!item) {
                  return false;
                }
                return (
                  item
                    .toUpperCase()
                    .indexOf(this.keyword.trim().toUpperCase()) !== -1
                );
              });
            },
          );
          this.searchedEquipmentVessels = filteredEquipmentVessels;
          break;
        }
        // 石材ストック
        case 1: {
          const filteredBaseStations = this.getSortedBaseStations.filter(
            (base) => {
              const searchTargetArray = [base.label];
              return searchTargetArray.some((item) => {
                if (!item) {
                  return false;
                }
                return (
                  item
                    .toUpperCase()
                    .indexOf(this.keyword.trim().toUpperCase()) !== -1
                );
              });
            },
          );
          this.searchedBaseStations = filteredBaseStations;
          break;
        }
        // 投入実績
        case 2: {
          const filteredEquipmentTypes = this.getEquipmentTypes.filter(
            (equipmentType) => {
              const searchTargetArray = [equipmentType.label];
              return searchTargetArray.some((item) => {
                if (!item) {
                  return false;
                }
                return (
                  item
                    .toUpperCase()
                    .indexOf(this.keyword.trim().toUpperCase()) !== -1
                );
              });
            },
          );
          this.searchedEquipmentTypes = filteredEquipmentTypes;
          break;
        }
        default:
          break;
      }
    },
    // 選択されているタブの変更
    changeSelectedTabIndex(index) {
      this.selectedTabIndex = index;
    },
    // 作業船の座標に移動
    async jumpToEquipmentVessel(vesselInfo) {
      if (!this.mapId) return this.showFailureMessage();
      if (!vesselInfo || !vesselInfo.vesselId) {
        this.showFailureMessage();
        return;
      }
      const { vesselId } = vesselInfo;
      // 選択中のカードをクリックしたら選択を解除しデフォルトポジションに戻る
      if (this.trackingVesselTarget === vesselId) {
        this.UPDATE_LAST_TRACKING_POSITION({
          mapId: this.mapId,
          center: [],
        });
        return;
      }
      // storeにlastPositionデータを格納
      await this.fetchLastVesselPositions(vesselId);
      const coordinate = this.getLastVesselPosition(vesselId);

      if (
        coordinate &&
        coordinate.longitude !== null &&
        coordinate.latitude !== null
      ) {
        const center = [coordinate.longitude, coordinate.latitude];
        // store(vuex)更新
        this.UPDATE_LAST_TRACKING_POSITION({
          mapId: this.mapId,
          center: center,
        });
        this.UPDATE_GRID_MAP_SETTINGS({
          map_id: this.mapId,
          tracking_target: { type: "vessel", id: vesselId },
        });
        // firestore更新
        await FireStoreService.setGridMapSettings(this.mapId, {
          tracking_target: { type: "vessel", id: vesselId },
        });
        this.showNotifyTimeout(coordinate);
      } else {
        this.showFailureMessage();
      }
    },
    // 拠点の座標に移動
    async jumpToBaseStation(baseStation) {
      if (!this.mapId) return this.showFailureMessage();
      if (
        !baseStation ||
        !baseStation.hash ||
        !baseStation.point ||
        baseStation.point.longitude == null ||
        baseStation.point.latitude == null
      ) {
        this.showFailureMessage();
        return;
      }
      // 選択中のカードをクリックしたら選択を解除しデフォルトポジションに戻る
      if (this.trackingBaseTarget === baseStation.hash) {
        this.UPDATE_LAST_TRACKING_POSITION({
          mapId: this.mapId,
          center: [],
        });
        return;
      }
      const center = [baseStation.point.longitude, baseStation.point.latitude];
      // store(vuex)更新
      this.UPDATE_LAST_TRACKING_POSITION({
        mapId: this.mapId,
        center: center,
      });
      // 石材ストックのポップアップメッセージを表示
      // TODO: 一旦現在右メニューに表示中の住所のみ表示
      const popupObject = {
        closeOnClick: false,
        closeButton: true,
        offset: 15,
        className: "base-station-label-popup",
        maxWidth: "340px",
      };
      const text = `
                    <div class="popup-title">石材ストック</div>
                    <ul>
                    <li class="popup-text-content">${baseStation.label}</li>
                    </ul>
                    `;
      this.createExpiredPopup(popupObject, center, text);
      this.UPDATE_GRID_MAP_SETTINGS({
        map_id: this.mapId,
        tracking_target: { type: "base", id: baseStation.hash },
      });
      // firestore更新
      await FireStoreService.setGridMapSettings(this.mapId, {
        tracking_target: { type: "base", id: baseStation.hash },
      });
    },
    // グリッドマップにポップアップメッセージを表示
    createExpiredPopup(popupObject, center, text) {
      this.SET_EXPIRED_POPUP_FOR_GRID({
        mapId: this.activeSidebarForGrid.mapId,
        popupObject: popupObject,
        center: center,
        text: text,
      });
    },
    // 座標取得に失敗したときのメッセージ
    showFailureMessage() {
      this.$vs.notify({
        text: "位置情報が取得できません。",
        color: "danger",
        iconPack: "feather",
        icon: "icon-alert-circle",
        position: "top-center",
      });
    },
    // 最終計測時間が10分前より遅い場合はポップアップメッセージを表示する
    showNotifyTimeout(lastPosition) {
      if (!lastPosition.last_position_at) return;
      const tenMinutesAgo = moment().subtract(10, "minutes").utc().unix();
      const lastPosAt = moment(lastPosition.last_position_at).unix();

      if (lastPosAt < tenMinutesAgo) {
        const name = lastPosition.vessel_name_ja || lastPosition.vessel_name;
        const dateFormat = moment(lastPosition.last_position_at).format(
          "YYYY/MM/DD HH:mm",
        );
        let elapsed = DateUtil.elapsedTime(dateFormat);
        const popupObject = {
          closeOnClick: false,
          closeButton: true,
          offset: 15,
          className: "expired-vessel-live-position-popup",
        };
        const center = [lastPosition.longitude, lastPosition.latitude];
        const text = `${name}はこの位置で、${elapsed}(${dateFormat})に記録されました。`;
        this.createExpiredPopup(popupObject, center, text);
      }
    },
    hasVesselDetailOrRequesting(vesselId) {
      if (this.vesselDetailRequestingIds.has(vesselId)) return true;
      return this.vesselDetailsMap[vesselId] !== undefined;
    },
  },
  watch: {
    equipmentVessels: {
      immediate: true,
      deep: true,
      async handler() {
        // 作業船情報
        if (!this.equipmentVessels || !this.equipmentVessels.length) return;
        await Promise.allSettled(
          this.equipmentVessels
            .filter((vessel) => {
              return vessel && vessel.vessel_id;
            })
            .map(async (vessel) => {
              // 船舶情報を取得済みまたはリクエスト中の場合以外にAPIリクエストする
              if (!this.hasVesselDetailOrRequesting(vessel.vessel_id)) {
                // 船舶情報取得API
                this.vesselDetailRequestingIds.add(vessel.vessel_id);
                this.vesselDetails.push(await this.getVessel(vessel.vessel_id));
                this.vesselDetailRequestingIds.delete(vessel.vessel_id);
              }
            }),
        );
      },
    },
    // タブを変更したら検索欄を空にする。
    selectedTabIndex() {
      this.keyword = "";
    },
  },
  beforeDestroy() {
    if (this.timerId) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
    if (this.unsubscribes.length !== 0) {
      this.unsubscribes.forEach((unsubscribe) => unsubscribe());
      this.unsubscribes = [];
    }
    window.clearInterval(intervalEquipmentVesselInfoId);
  },
};
</script>

<style lang="scss">
@import "src/assets/scss/aisea/variables";
</style>
